/*
 * <knack-header> — behaviour for the Knack floating-pill header.
 *
 * Reimplemented natively (no libraries) as a Web Component:
 *   - Spotlight hover on the left nav (a pill highlight follows the cursor).
 *   - SHOP dropdown intent (stays open while the cursor travels to it).
 *   - Right-pill spotlight that rests on the cart and slides to the account link on hover.
 *   - Mobile full-screen overlay with focus trap, Escape to close, and focus return.
 *   - Closes the overlay and resets the spotlight on breakpoint resize.
 *
 * The cart pill itself opens Horizon's native <cart-drawer-component> via its own
 * on:click="/open" binding, so this component does not manage the cart drawer.
 */
class KnackHeader extends HTMLElement {
  connectedCallback() {
    this.mobileBreakpoint = parseInt(this.getAttribute('data-mobile-breakpoint') || '768', 10);
    this.lastFocused = null;

    this.leftPill = this.querySelector('.knack-header__left');
    this.navWrap = this.querySelector('.knack-header__nav-wrap');
    this.navHighlight = this.querySelector('.knack-header__nav-hl');
    this.navLinks = this.querySelectorAll('.knack-header__nav-item > a');

    this.rightPill = this.querySelector('.knack-header__right');
    this.rightHighlight = this.querySelector('.knack-header__right-hl');
    this.accountLink = this.querySelector('.knack-header__account');
    this.cartLink = this.querySelector('.knack-header__cart');

    this.dropParent = this.querySelector('.knack-header__nav-item--has-drop');
    this.dropdown = this.dropParent ? this.dropParent.querySelector('.knack-header__dropdown') : null;

    this.burger = this.querySelector('.knack-header__burger');
    this.overlay = this.querySelector('.knack-header__overlay');
    this.overlayClose = this.overlay ? this.overlay.querySelector('.knack-header__overlay-close') : null;

    this.#bindNavSpotlight();
    this.#bindDropdown();
    this.#bindRightSpotlight();
    this.#bindMobile();
    this.#bindOverlap();

    this.#onResize = this.#handleResize.bind(this);
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
    clearTimeout(this.#dropTimer);
  }

  #dropTimer = 0;
  #onResize = null;

  /* ── Left nav spotlight ──────────────────────────────────── */
  #bindNavSpotlight() {
    if (!this.leftPill || !this.navWrap || !this.navHighlight || !this.navLinks.length) return;

    // The highlight is positioned relative to the left pill (its containing block) so it tracks
    // the links even though the nav wrap is now static (which lets the dropdown align to the logo).
    this.navLinks.forEach((link) => {
      link.addEventListener('mouseenter', () => this.#moveHighlight(this.leftPill, this.navHighlight, link, true));
      link.addEventListener('focus', () => this.#moveHighlight(this.leftPill, this.navHighlight, link, true));
    });

    this.navWrap.addEventListener('mouseleave', () => {
      this.navHighlight.style.opacity = '0';
    });
    this.navWrap.addEventListener('focusout', (event) => {
      if (!this.navWrap.contains(event.relatedTarget)) this.navHighlight.style.opacity = '0';
    });
  }

  #moveHighlight(wrap, highlight, target, show) {
    const wRect = wrap.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    highlight.style.left = `${tRect.left - wRect.left}px`;
    highlight.style.top = `${tRect.top - wRect.top}px`;
    highlight.style.width = `${tRect.width}px`;
    highlight.style.height = `${tRect.height}px`;
    if (show) highlight.style.opacity = '1';
  }

  /* ── SHOP dropdown intent ────────────────────────────────── */
  #bindDropdown() {
    if (!this.dropParent || !this.dropdown) return;
    const trigger = this.dropParent.querySelector('a');

    const open = () => {
      clearTimeout(this.#dropTimer);
      this.dropParent.classList.add('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    };
    const scheduleClose = () => {
      this.#dropTimer = setTimeout(() => {
        this.dropParent.classList.remove('is-open');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }, 140);
    };

    this.dropParent.addEventListener('mouseenter', open);
    this.dropParent.addEventListener('mouseleave', scheduleClose);
    // Keyboard: open on focus within the trigger, close when focus leaves the item.
    this.dropParent.addEventListener('focusin', open);
    this.dropParent.addEventListener('focusout', (event) => {
      if (!this.dropParent.contains(event.relatedTarget)) {
        this.dropParent.classList.remove('is-open');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /* ── Right-pill spotlight (rests on cart, slides to account) ─ */
  #bindRightSpotlight() {
    if (!this.rightPill || !this.rightHighlight || !this.cartLink) return;

    const resetToCart = () => {
      this.#moveHighlight(this.rightPill, this.rightHighlight, this.cartLink, false);
      this.rightPill.classList.remove('is-account');
    };

    // Position once layout + fonts have settled so the pill is measured correctly.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(resetToCart);
    } else {
      requestAnimationFrame(resetToCart);
    }
    this.#resetRight = resetToCart;

    if (this.accountLink) {
      this.accountLink.addEventListener('mouseenter', () => {
        this.#moveHighlight(this.rightPill, this.rightHighlight, this.accountLink, false);
        this.rightPill.classList.add('is-account');
      });
      this.accountLink.addEventListener('focus', () => {
        this.#moveHighlight(this.rightPill, this.rightHighlight, this.accountLink, false);
        this.rightPill.classList.add('is-account');
      });
      this.accountLink.addEventListener('mouseleave', resetToCart);
      this.accountLink.addEventListener('blur', resetToCart);
    }
  }

  #resetRight = null;

  /* ── Mobile overlay ──────────────────────────────────────── */
  #bindMobile() {
    if (!this.burger || !this.overlay) return;

    this.burger.addEventListener('click', () => this.openOverlay());
    if (this.overlayClose) this.overlayClose.addEventListener('click', () => this.closeOverlay());

    this.overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeOverlay();
        return;
      }
      if (event.key === 'Tab') this.#trapFocus(event);
    });
  }

  #focusable() {
    return Array.from(
      this.overlay.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
  }

  #trapFocus(event) {
    const items = this.#focusable();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  openOverlay() {
    this.lastFocused = document.activeElement;
    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (this.burger) this.burger.setAttribute('aria-expanded', 'true');
    // Move focus into the overlay (close button first).
    const target = this.overlayClose || this.#focusable()[0];
    if (target) target.focus();
  }

  closeOverlay() {
    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (this.burger) this.burger.setAttribute('aria-expanded', 'false');
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }
  }

  /* ── Hero overlap (opt-in: float the transparent bar over the next section) ─ */
  #overlapApply = null;

  #bindOverlap() {
    if (this.getAttribute('data-overlap') !== 'true') return;
    const apply = () => {
      // Reset, measure the bar's own height, then pull the following content up by it.
      this.style.marginBottom = '';
      const h = Math.round(this.getBoundingClientRect().height);
      this.style.marginBottom = `-${h}px`;
      // Publish the measured height so anything that has to clear the floating bar can use the
      // real number instead of its own hard-coded guess. The breadcrumb used to carry a fixed
      // 90px, calibrated against a ~88-89px retail bar; a trade bar is taller (the company name
      // and location stack to two lines, ~103px), so the trail ended up tucked behind it.
      document.documentElement.style.setProperty('--knack-header-h', `${h}px`);
    };
    this.#overlapApply = apply;
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(apply);
    }
    requestAnimationFrame(apply);
    // rAF is suspended in a background tab, and this has been seen loading with no overlap applied
    // at all (the bar's own height left as dead space under it). Run once now as well, so the
    // overlap is never dependent on a frame being painted; the callbacks above then correct the
    // measurement once fonts settle.
    apply();
  }

  #handleResize() {
    if (window.innerWidth > this.mobileBreakpoint) {
      if (this.overlay && this.overlay.classList.contains('is-open')) this.closeOverlay();
      if (this.#resetRight) this.#resetRight();
    }
    if (this.#overlapApply) this.#overlapApply();
  }
}

if (!customElements.get('knack-header')) {
  customElements.define('knack-header', KnackHeader);
}
